import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { analyticsAPI, projectAPI, leadAPI, micrositeAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Briefcase, Globe, DollarSign, TrendingUp, Plus, ArrowUpRight, 
  Building2, Eye, Users, Phone, Calendar, CheckCircle2, Clock,
  IndianRupee, Target, BarChart3, FileText, MapPin, Receipt,
  AlertCircle, Star
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const PartnerDashboard = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [microsites, setMicrosites] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPartner = hasRole(['partner']);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load CP-specific dashboard data
      const [statsRes, projectsRes, leadsRes, micrositesRes] = await Promise.all([
        fetch(`${API}/api/analytics/cp-dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        projectAPI.list(),
        leadAPI.list({}),
        micrositeAPI.list(),
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      setProjects(projectsRes.data.slice(0, 4));
      const leadsData = leadsRes.data.leads || leadsRes.data;
      setRecentLeads(Array.isArray(leadsData) ? leadsData.slice(0, 5) : []);
      setMicrosites(micrositesRes.data.slice(0, 3));
      
      // Load team stats if CP has sub-users
      if (isPartner) {
        const teamRes = await fetch(`${API}/api/analytics/team-performance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (teamRes.ok) {
          const teamData = await teamRes.json();
          setTeamStats(teamData);
        }
      }
    } catch (error) {
      console.error('Dashboard load error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
    return `₹${amount.toLocaleString()}`;
  };

  const getLeadStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-700',
      'contacted': 'bg-yellow-100 text-yellow-700',
      'qualified': 'bg-emerald-100 text-emerald-700',
      'site_visit_scheduled': 'bg-violet-100 text-violet-700',
      'site_visit_completed': 'bg-cyan-100 text-cyan-700',
      'negotiation': 'bg-orange-100 text-orange-700',
      'booked': 'bg-green-100 text-green-700',
      'lost': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </div>
    );
  }

  const pipeline = stats?.pipeline || {};

  return (
    <div className="space-y-6" data-testid="partner-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Welcome, {user?.name}</h1>
          <p className="text-muted-foreground mt-1">Your performance dashboard</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/partner/leads')} variant="outline" data-testid="btn-add-lead">
            <Plus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
          <Button onClick={() => navigate('/partner/microsites')} data-testid="btn-create-microsite">
            <Globe className="h-4 w-4 mr-2" />
            Create Microsite
          </Button>
        </div>
      </div>

      {/* Key Metrics - Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* My Leads */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Briefcase className="h-8 w-8 text-blue-600" />
              <Badge variant="outline" className="bg-blue-500 text-white border-0">Total</Badge>
            </div>
            <div className="mt-3">
              <p className="text-3xl font-bold text-blue-700">{stats?.total_leads || 0}</p>
              <p className="text-sm text-blue-600">My Leads</p>
              <p className="text-xs text-blue-500 mt-1">{stats?.new_leads || 0} new this week</p>
            </div>
          </CardContent>
        </Card>

        {/* Site Visits */}
        <Card className="bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <MapPin className="h-8 w-8 text-violet-600" />
              <Badge variant="outline" className="bg-violet-500 text-white border-0">Visits</Badge>
            </div>
            <div className="mt-3">
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold text-violet-700">{stats?.site_visits_completed || 0}</p>
                <p className="text-lg text-violet-500">/ {stats?.site_visits_scheduled || 0}</p>
              </div>
              <p className="text-sm text-violet-600">Done / Scheduled</p>
              <p className="text-xs text-violet-500 mt-1">{stats?.site_visits_today || 0} today</p>
            </div>
          </CardContent>
        </Card>

        {/* Bookings */}
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              <Badge variant="outline" className="bg-emerald-500 text-white border-0">Closed</Badge>
            </div>
            <div className="mt-3">
              <p className="text-3xl font-bold text-emerald-700">{stats?.bookings || 0}</p>
              <p className="text-sm text-emerald-600">Total Bookings</p>
              <p className="text-xs text-emerald-500 mt-1">{stats?.bookings_this_month || 0} this month</p>
            </div>
          </CardContent>
        </Card>

        {/* Pending Invoices */}
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Receipt className="h-8 w-8 text-amber-600" />
              <Badge variant="outline" className="bg-amber-500 text-white border-0">Action</Badge>
            </div>
            <div className="mt-3">
              <p className="text-3xl font-bold text-amber-700">{stats?.pending_invoices || 0}</p>
              <p className="text-sm text-amber-600">Pending Invoices</p>
              <Button 
                variant="link" 
                className="p-0 h-auto text-xs text-amber-700"
                onClick={() => navigate('/partner/invoices')}
              >
                Create invoice →
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Earnings & Commissions - Row 2 */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Commission Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-green-600" />
              My Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Total Earned</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(stats?.total_commission)}</p>
                </div>
                <IndianRupee className="h-10 w-10 text-green-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-emerald-600">{formatCurrency(stats?.paid_commission)}</p>
                  <p className="text-xs text-emerald-600">Received</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-amber-600">{formatCurrency(stats?.pending_commission)}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Microsite Performance */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="h-5 w-5 text-cyan-600" />
              Microsite Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-cyan-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                  <p className="text-2xl font-bold text-cyan-600">{stats?.microsite_views || 0}</p>
                </div>
                <Eye className="h-10 w-10 text-cyan-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/30 rounded-lg text-center">
                  <p className="text-lg font-bold">{stats?.microsites || 0}</p>
                  <p className="text-xs text-muted-foreground">Active Sites</p>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg text-center">
                  <p className="text-lg font-bold">{stats?.microsite_leads || 0}</p>
                  <p className="text-xs text-muted-foreground">Leads Generated</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* My Ranking */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-600" />
              My Ranking
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Leaderboard Position</p>
                  <p className="text-2xl font-bold text-amber-600">#{stats?.rank || '-'}</p>
                </div>
                <Target className="h-10 w-10 text-amber-500" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Monthly Target</span>
                  <span className="font-medium">{stats?.target_progress || 0}%</span>
                </div>
                <Progress value={stats?.target_progress || 0} className="h-2" />
              </div>
              <Button 
                variant="outline" 
                className="w-full" 
                size="sm"
                onClick={() => navigate('/partner/leaderboard')}
              >
                View Leaderboard
                <ArrowUpRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lead Pipeline */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                My Lead Pipeline
              </CardTitle>
              <CardDescription>{stats?.total_leads || 0} total leads</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/partner/crm')}>
              View CRM
              <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {[
              { key: 'new', label: 'New', color: 'bg-blue-500' },
              { key: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
              { key: 'qualified', label: 'Qualified', color: 'bg-purple-500' },
              { key: 'site_visit_scheduled', label: 'SV Sched', color: 'bg-indigo-500' },
              { key: 'site_visit_completed', label: 'SV Done', color: 'bg-cyan-500' },
              { key: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
              { key: 'booked', label: 'Booked', color: 'bg-green-500' },
              { key: 'lost', label: 'Lost', color: 'bg-red-500' },
            ].map((stage) => (
              <div 
                key={stage.key} 
                className="text-center p-3 rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/partner/crm?status=${stage.key}`)}
              >
                <div className={`w-3 h-3 rounded-full ${stage.color} mx-auto mb-2`}></div>
                <p className="text-xl font-bold">{pipeline[stage.key] || 0}</p>
                <p className="text-xs text-muted-foreground">{stage.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Team Performance (Only for CP with sub-users) */}
      {isPartner && teamStats && teamStats.team_members && teamStats.team_members.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  My Team Performance
                </CardTitle>
                <CardDescription>{teamStats.team_members.length} team members</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/partner/team')}>
                Manage Team
                <ArrowUpRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {teamStats.team_members.slice(0, 5).map((member, idx) => (
                <div 
                  key={member.id} 
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-500'
                    }`}>
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-muted-foreground">{member.leads_count} leads</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">{member.bookings_count} bookings</p>
                    <p className="text-xs text-muted-foreground">{member.conversion_rate}% conversion</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions & Recent Leads */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Recent Leads</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/partner/leads')}>
              View All <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Briefcase className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No leads yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/partner/leads')}>
                  Add your first lead
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentLeads.map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">{lead.name}</p>
                      <p className="text-sm text-muted-foreground">{lead.phone}</p>
                    </div>
                    <Badge className={getLeadStatusColor(lead.status)}>
                      {lead.status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">My Microsites</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/partner/microsites')}>
              View All <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {microsites.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Globe className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No microsites created</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/partner/microsites')}>
                  Create your first microsite
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {microsites.map((site) => (
                  <div key={site.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">{site.title}</p>
                      <p className="text-sm text-muted-foreground">/{site.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={site.status === 'published' ? 'default' : 'secondary'}>
                        {site.status}
                      </Badge>
                      <span className="flex items-center text-sm text-muted-foreground">
                        <Eye className="h-3 w-3 mr-1" />
                        {site.analytics?.views || 0}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Available Projects */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Available Projects</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/partner/projects')}>
            View All <ArrowUpRight className="h-4 w-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No projects assigned yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {projects.map((project) => (
                <div 
                  key={project.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/partner/projects/${project.id}`)}
                >
                  <Building2 className="h-8 w-8 text-amber-600 mb-3" />
                  <p className="font-semibold">{project.name}</p>
                  <p className="text-sm text-muted-foreground">{project.location || project.city}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerDashboard;
